import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Shield, User, Eye, Crown, CheckCircle2 } from "lucide-react";

const ROLE_META = {
  owner:  { label: "Owner",  icon: Crown,  cls: "text-amber-700" },
  admin:  { label: "Admin",  icon: Shield, cls: "text-blue-700" },
  member: { label: "Member", icon: User,   cls: "text-emerald-700" },
  viewer: { label: "Viewer", icon: Eye,    cls: "text-slate-600" },
};

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/account/invites/inspect/${token}`)
      .then((r) => {
        setInvite(r.data);
        setName(r.data.name_hint || "");
      })
      .catch((e) => setLoadErr(e?.response?.data?.detail || "This link is invalid or expired."));
  }, [token]);

  const accept = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const payload = { token };
      if (!invite.user_exists) {
        if (!name.trim() || !password) {
          toast.error("Name and password are required to create your account");
          setSubmitting(false);
          return;
        }
        if (password.length < 6) {
          toast.error("Password must be at least 6 characters");
          setSubmitting(false);
          return;
        }
        payload.name = name.trim();
        payload.password = password;
      }
      const r = await api.post("/account/invites/accept", payload);
      // Stash the token so the user is logged in to the new account immediately.
      localStorage.setItem("sw_token", r.data.token);
      localStorage.setItem("sw_user", JSON.stringify(r.data.user));
      setDone(true);
      // Brief confirmation, then take them to the dashboard.
      setTimeout(() => { window.location.href = "/app/dashboard"; }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invitation");
      setSubmitting(false);
    }
  };

  if (loadErr) {
    return (
      <Page>
        <div className="text-center">
          <div className="w-14 h-14 mx-auto bg-red-50 rounded-full grid place-items-center mb-3">
            <Mail size={20} className="text-red-600" />
          </div>
          <h2 className="text-[18px] font-bold text-slate-900">Invitation unavailable</h2>
          <p className="text-[13.5px] text-slate-600 mt-2">{loadErr}</p>
          <Link to="/login" className="inline-block mt-5 text-[13px] text-blue-600 hover:underline">Go to sign in</Link>
        </div>
      </Page>
    );
  }
  if (!invite) {
    return <Page><div className="text-center text-slate-400"><Loader2 size={20} className="animate-spin mx-auto" /></div></Page>;
  }
  if (done) {
    return (
      <Page>
        <div className="text-center">
          <div className="w-14 h-14 mx-auto bg-emerald-50 rounded-full grid place-items-center mb-3">
            <CheckCircle2 size={22} className="text-emerald-600" />
          </div>
          <h2 className="text-[18px] font-bold text-slate-900">You're in!</h2>
          <p className="text-[13.5px] text-slate-600 mt-2">Loading your workspace…</p>
        </div>
      </Page>
    );
  }

  const meta = ROLE_META[invite.role] || ROLE_META.member;
  const Icon = meta.icon;

  return (
    <Page>
      <div className="text-center mb-6">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">Brainydo · Team invitation</div>
        <h1 className="text-[22px] font-extrabold text-slate-900">Join {invite.account_name}</h1>
        <p className="text-[13.5px] text-slate-600 mt-2">
          <strong>{invite.invited_by_name || "A teammate"}</strong> invited <strong>{invite.email}</strong> to this workspace as a{" "}
          <span className={"inline-flex items-center gap-1 font-semibold " + meta.cls}>
            <Icon size={13} /> {meta.label}
          </span>.
        </p>
      </div>

      <form onSubmit={accept} className="space-y-4">
        {!invite.user_exists ? (
          <>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Your name</label>
              <input
                type="text" required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-slate-900 outline-none"
                data-testid="accept-name"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Set a password</label>
              <input
                type="password" required minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-slate-900 outline-none"
                data-testid="accept-password"
              />
            </div>
          </>
        ) : (
          <p className="text-[13px] text-slate-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            You already have a Brainydo account with this email. Click below to join {invite.account_name} —
            you'll stay signed in.
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full h-11 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[14px] font-semibold disabled:opacity-60 transition active:scale-[0.99]"
          data-testid="accept-submit"
        >
          {submitting && <Loader2 size={15} className="animate-spin" />}
          {invite.user_exists ? "Join workspace" : "Create account & join"}
        </button>

        <p className="text-[11.5px] text-slate-400 text-center pt-1">
          By accepting you agree to the workspace owner managing data for this account.
        </p>
      </form>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div className="min-h-screen bg-[#faf8ff] grid place-items-center p-6">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 w-full max-w-md">
        {children}
      </div>
    </div>
  );
}
